// ORG-CHART-001 Sprint 0153 — Designation org chart, rewritten.
//
// Reads from /hr/org/org-chart (designation source of truth, post-0153)
// instead of /hr/role-master/org-chart. The new endpoint honours the
// hide_from_org_chart / legacy_role_flag / alias_of_designation_code
// metadata so the default view shows only the canonical Sprint-0084
// tree with no orphan-at-root mess.
//
// Toggles:
//   - "Hide details-pending" (default ON) — pushes hide_pending=true
//     to the server; the server joins to RoleResponsibilityMaster and
//     filters approval_status='DETAILS_PENDING' rows out.
//   - "Hide legacy / aliased" (default ON) — pushes hide_legacy=true;
//     hides the RBAC-aligned codes that were ALIAS_AND_HIDE'd in seed.
//
// Search filters client-side over designation_name + designation_code.
//
// Group-by-function: builds super-headers per function_group
// (BOARD, FINANCE, HR, OPERATIONS, STORES, QUALITY, TECHNICAL,
//  PROCUREMENT, SALES, IT, SECURITY, ADMIN), each collapsible.
//
// Orphan badge: pulls /hr/org/orphans on mount; clicking opens a
// drawer with the orphan list.
//
// Validate button: pulls /hr/org/validate; reports cycles + multi-root
// in a drawer.
//
// Click-through: navigates to /hr/role-master/:role_code (existing
// detail page works for both designation and role-master codes).

import { useMemo, useState } from 'react'
import {
  App, Badge, Button, Card, Checkbox, Drawer, Empty, Input, Modal, Select,
  Space, Switch, Tag, Tree, Typography,
} from 'antd'
import type { DataNode } from 'antd/es/tree'
import {
  WarningOutlined, CheckCircleTwoTone, SearchOutlined, UserAddOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  hrOrgApi, type AssignPersonResult, type DesignationNode,
  type FunctionGroup,
} from '@/features/hr/hrOrgApi'
import apiClient from '@/shared/api/client'
import PageHeader from '@/shared/components/PageHeader'
import { displayEmpCode } from '@/shared/utils/empCode'

const { Text } = Typography

const CRITICALITY_COLORS: Record<string, string> = {
  CRITICAL: 'red', HIGH: 'volcano', MEDIUM: 'gold', LOW: 'green',
}

// Display order + label for the function-group super-headers.
const FUNCTION_GROUPS: { code: FunctionGroup; label: string; icon: string }[] = [
  { code: 'BOARD',       label: 'Board & Executive', icon: '🏛️' },
  { code: 'FINANCE',     label: 'Finance',            icon: '💰' },
  { code: 'HR',          label: 'HR',                 icon: '👥' },
  { code: 'ADMIN',       label: 'Admin / Welfare',    icon: '🧹' },
  { code: 'SECURITY',    label: 'Security & Gate',    icon: '🔒' },
  { code: 'OPERATIONS',  label: 'Operations / Production', icon: '⚙️' },
  { code: 'STORES',      label: 'Stores / Warehouse', icon: '📦' },
  { code: 'QUALITY',     label: 'Quality',            icon: '✅' },
  { code: 'TECHNICAL',   label: 'Technical / Engineering', icon: '🔧' },
  { code: 'PROCUREMENT', label: 'Procurement',        icon: '🛒' },
  { code: 'SALES',       label: 'Sales',              icon: '📈' },
  { code: 'IT',          label: 'IT / ERP / Audit',   icon: '💻' },
]

export default function OrgChartPage() {
  const navigate = useNavigate()
  const { message: msg } = App.useApp()
  const qc = useQueryClient()

  // Filter toggles
  const [hidePending, setHidePending] = useState(true)
  const [hideLegacy, setHideLegacy] = useState(true)
  const [search, setSearch] = useState('')

  // Drawers
  const [orphanDrawerOpen, setOrphanDrawerOpen] = useState(false)
  const [validateDrawerOpen, setValidateDrawerOpen] = useState(false)

  // Assign-person modal — target node + form state
  const [assignTarget, setAssignTarget] = useState<DesignationNode | null>(null)
  const [assignEmployeeId, setAssignEmployeeId] = useState<string | undefined>()
  const [createLogin, setCreateLogin] = useState(true)
  // One-time password result modal (shown after a login is created)
  const [passwordResult, setPasswordResult] = useState<AssignPersonResult | null>(null)

  // Main tree
  const { data: tree = [], isLoading } = useQuery({
    queryKey: ['org-chart', hidePending, hideLegacy],
    queryFn: () => hrOrgApi.orgChart(hidePending, hideLegacy),
  })

  // Orphan audit (cheap, pulled once per page load + on toggle change)
  const { data: orphanAudit } = useQuery({
    queryKey: ['org-chart-orphans'],
    queryFn: () => hrOrgApi.orphans(),
  })

  // Hierarchy validation
  const { data: validation } = useQuery({
    queryKey: ['org-chart-validation'],
    queryFn: () => hrOrgApi.validate(),
    enabled: validateDrawerOpen, // only fetch when drawer opens
  })

  // Active employees for the assign picker — fetched only while the modal
  // is open. Reuses /hr/employees (same source as the supervisor picker).
  const { data: empList, isLoading: empLoading } = useQuery<
    { id: string; emp_code: string; emp_name: string }[]
  >({
    queryKey: ['org-chart-assign-employees'],
    queryFn: () =>
      apiClient
        .get('/hr/employees', { params: { size: 500, is_active: true } })
        .then((r) => r.data.items ?? []),
    enabled: assignTarget !== null,
  })

  const closeAssign = () => {
    setAssignTarget(null)
    setAssignEmployeeId(undefined)
    setCreateLogin(true)
  }

  const assignMut = useMutation({
    mutationFn: (payload: {
      employee_id: string
      designation_code: string
      create_login: boolean
    }) => hrOrgApi.assignPerson(payload),
    onSuccess: (res) => {
      const empName =
        empList?.find((e) => e.id === assignEmployeeId)?.emp_name ?? 'Employee'
      closeAssign()
      // Refresh the chart + orphan audit so counts reflect the change.
      qc.invalidateQueries({ queryKey: ['org-chart'] })
      qc.invalidateQueries({ queryKey: ['org-chart-orphans'] })
      if (res.login?.temp_password) {
        // A fresh login was created — show the one-time password once.
        setPasswordResult(res)
      } else {
        msg.success(`Assigned ${empName} to ${res.designation_name}`)
      }
    },
    onError: (e: unknown) => {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'Assign failed'
      msg.error(detail)
    },
  })


  // ── Flatten the tree into a per-function-group grouping ─────────
  // The API returns roots that have function_group on each node.
  // We bucket the roots by function_group and render each bucket as
  // its own AntD Tree under a collapsible Card super-header.
  const groupedTree = useMemo(() => {
    const buckets = new Map<FunctionGroup | '_OTHER', DesignationNode[]>()
    for (const root of tree) {
      const fg = (root.function_group || '_OTHER') as FunctionGroup | '_OTHER'
      const arr = buckets.get(fg) ?? []
      arr.push(root)
      buckets.set(fg, arr)
    }
    return buckets
  }, [tree])

  // Client-side search filter — keeps any node whose name OR code
  // matches, plus all its ancestors (so the path stays visible).
  const matchesSearch = (n: DesignationNode): boolean => {
    if (!search.trim()) return true
    const q = search.trim().toLowerCase()
    if (n.designation_name.toLowerCase().includes(q)) return true
    if (n.designation_code.toLowerCase().includes(q)) return true
    return n.children.some(matchesSearch)
  }

  function nodeToTree(n: DesignationNode): DataNode | null {
    if (!matchesSearch(n)) return null
    const visibleChildren = n.children
      .map(nodeToTree)
      .filter(Boolean) as DataNode[]
    return {
      key: n.id,
      title: (
        <Space size={4} wrap>
          <Text strong>{n.designation_name}</Text>
          <Text type="secondary" code style={{ fontSize: 11 }}>
            {n.designation_code}
          </Text>
          {n.criticality && (
            <Tag color={CRITICALITY_COLORS[n.criticality]}>{n.criticality}</Tag>
          )}
          {n.statutory_role_flag && <Tag color="purple">Statutory</Tag>}
          {n.legacy_role_flag && <Tag color="default">Legacy</Tag>}
          {n.alias_of_designation_code && (
            <Tag color="default">→ {n.alias_of_designation_code}</Tag>
          )}
          <Button
            size="small"
            type="link"
            icon={<UserAddOutlined />}
            onClick={(e) => {
              // Don't let the click bubble to the Tree node select (which
              // navigates to the role detail page).
              e.stopPropagation()
              setAssignEmployeeId(undefined)
              setCreateLogin(true)
              setAssignTarget(n)
            }}
          >
            Assign
          </Button>
        </Space>
      ),
      children: visibleChildren,
    }
  }

  function findNode(nodes: DesignationNode[], id: string): DesignationNode | null {
    for (const n of nodes) {
      if (n.id === id) return n
      const found = findNode(n.children, id)
      if (found) return found
    }
    return null
  }

  const orphanCount = orphanAudit?.total_orphans ?? 0
  const visibleNodes = orphanAudit?.total_active_designations ?? 0

  return (
    <>
      <PageHeader
        title="Organisation Chart"
        subtitle={`Designation hierarchy. ${visibleNodes} active designations. Click a node to open the role.`}
        extra={
          <Space wrap>
            <Badge count={orphanCount} offset={[-4, 4]}>
              <Button
                icon={<WarningOutlined />}
                onClick={() => setOrphanDrawerOpen(true)}
                danger={orphanCount > 0}
              >
                Orphans
              </Button>
            </Badge>
            <Button
              icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
              onClick={() => setValidateDrawerOpen(true)}
            >
              Validate
            </Button>
          </Space>
        }
      />

      {/* Filter strip */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap size="middle">
          <Input
            placeholder="Search role name or code"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ width: 280 }}
          />
          <Space size={4}>
            <Switch
              size="small"
              checked={hidePending}
              onChange={setHidePending}
            />
            <Text type="secondary">Hide details-pending</Text>
          </Space>
          <Space size={4}>
            <Switch
              size="small"
              checked={hideLegacy}
              onChange={setHideLegacy}
            />
            <Text type="secondary">Hide legacy / aliased</Text>
          </Space>
        </Space>
      </Card>

      {isLoading ? (
        <Text>Loading…</Text>
      ) : tree.length === 0 ? (
        <Empty description="No designations seeded yet" />
      ) : (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {/* Render one collapsible card per function group, in the
              canonical order, skipping empty buckets. */}
          {FUNCTION_GROUPS.map(({ code, label, icon }) => {
            const roots = groupedTree.get(code) ?? []
            if (roots.length === 0) return null
            const treeData = roots
              .map(nodeToTree)
              .filter(Boolean) as DataNode[]
            if (treeData.length === 0) return null
            return (
              <Card
                key={code}
                size="small"
                title={
                  <Space>
                    <span style={{ fontSize: 16 }}>{icon}</span>
                    <Text strong>{label}</Text>
                    <Tag>{roots.length} root{roots.length === 1 ? '' : 's'}</Tag>
                  </Space>
                }
                styles={{ body: { paddingTop: 6 } }}
              >
                <Tree
                  treeData={treeData}
                  // Expand top 3 levels by default; everything deeper
                  // stays collapsed so the chart fits on one screen.
                  defaultExpandedKeys={collectKeysToLevel(roots, 3)}
                  showLine
                  onSelect={(keys) => {
                    const id = keys[0]
                    if (!id) return
                    const node = findNode(roots, String(id))
                    if (node) navigate(`/hr/role-master/${node.designation_code}`)
                    else msg.error('Role not found')
                  }}
                />
              </Card>
            )
          })}

          {/* Other / ungrouped — only if any */}
          {(groupedTree.get('_OTHER')?.length ?? 0) > 0 && (
            <Card size="small" title={<Text strong>Other / Ungrouped</Text>}>
              <Tree
                treeData={
                  (groupedTree.get('_OTHER') ?? [])
                    .map(nodeToTree)
                    .filter(Boolean) as DataNode[]
                }
                showLine
                defaultExpandAll
                onSelect={(keys) => {
                  const id = keys[0]
                  if (!id) return
                  const node = findNode(groupedTree.get('_OTHER') ?? [], String(id))
                  if (node) navigate(`/hr/role-master/${node.designation_code}`)
                }}
              />
            </Card>
          )}
        </Space>
      )}

      {/* ── Orphan drawer ─────────────────────────────────────────── */}
      <Drawer
        title="Orphan audit"
        width={520}
        open={orphanDrawerOpen}
        onClose={() => setOrphanDrawerOpen(false)}
      >
        {orphanAudit == null ? (
          <Text>Loading…</Text>
        ) : orphanAudit.total_orphans === 0 ? (
          <Empty
            description="No orphans found — every designation is properly parented."
          />
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Text>
              {orphanAudit.total_orphans} orphan(s) found across{' '}
              {orphanAudit.total_active_designations} active designations.
            </Text>
            {orphanAudit.unparented.length > 0 && (
              <OrphanSection
                title="Unparented (no reports_to)"
                entries={orphanAudit.unparented}
              />
            )}
            {orphanAudit.missing_parent.length > 0 && (
              <OrphanSection
                title="Missing parent (broken reports_to)"
                entries={orphanAudit.missing_parent}
              />
            )}
            {orphanAudit.self_parent.length > 0 && (
              <OrphanSection
                title="Self-parent (reports to itself)"
                entries={orphanAudit.self_parent}
              />
            )}
          </Space>
        )}
      </Drawer>

      {/* ── Validate drawer ───────────────────────────────────────── */}
      <Drawer
        title="Hierarchy validation"
        width={520}
        open={validateDrawerOpen}
        onClose={() => setValidateDrawerOpen(false)}
      >
        {validation == null ? (
          <Text>Loading…</Text>
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Card size="small">
              <Space size="middle" wrap>
                <Tag color="blue">Total nodes: {validation.total_nodes}</Tag>
                <Tag color="green">Visible: {validation.visible_nodes}</Tag>
                <Tag>Max depth: {validation.max_depth}</Tag>
              </Space>
            </Card>
            {validation.cycles.length > 0 ? (
              <Card size="small" title="🚨 Cycles detected">
                {validation.cycles.map((cycle, i) => (
                  <div key={i}>
                    <Text code>{cycle.join(' → ')} → {cycle[0]}</Text>
                  </div>
                ))}
              </Card>
            ) : (
              <Card size="small">
                <Text type="success">✅ No cycles detected.</Text>
              </Card>
            )}
            {validation.multi_root.length > 0 ? (
              <Card size="small" title="⚠️ Multi-root anomalies">
                {validation.multi_root.map((m) => (
                  <div key={m.designation_code}>
                    <Text strong>{m.designation_name}</Text>{' '}
                    <Text code>{m.designation_code}</Text>{' '}
                    <Tag>{m.function_group}</Tag>
                  </div>
                ))}
              </Card>
            ) : (
              <Card size="small">
                <Text type="success">
                  ✅ Only CHAIRMAN is a root. Hierarchy is single-rooted.
                </Text>
              </Card>
            )}
          </Space>
        )}
      </Drawer>

      {/* ── Assign person modal ───────────────────────────────────────── */}
      <Modal
        title="Assign person to role"
        open={assignTarget !== null}
        onCancel={closeAssign}
        okText="Assign"
        okButtonProps={{ disabled: !assignEmployeeId }}
        confirmLoading={assignMut.isPending}
        onOk={() => {
          if (!assignTarget || !assignEmployeeId) return
          assignMut.mutate({
            employee_id: assignEmployeeId,
            designation_code: assignTarget.designation_code,
            create_login: createLogin,
          })
        }}
      >
        {assignTarget && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Text type="secondary">Role</Text>
              <div>
                <Text strong>{assignTarget.designation_name}</Text>{' '}
                <Text type="secondary" code style={{ fontSize: 11 }}>
                  {assignTarget.designation_code}
                </Text>
              </div>
            </div>
            <div>
              <Text type="secondary">Employee</Text>
              <Select
                showSearch
                allowClear
                loading={empLoading}
                placeholder="Search by name or code"
                style={{ width: '100%', marginTop: 4 }}
                value={assignEmployeeId}
                onChange={(v) => setAssignEmployeeId(v)}
                optionFilterProp="label"
                options={(empList ?? []).map((e) => ({
                  value: e.id,
                  label: `${e.emp_name} (${displayEmpCode(e.emp_code)})`,
                }))}
              />
            </div>
            <Checkbox
              checked={createLogin}
              onChange={(e) => setCreateLogin(e.target.checked)}
            >
              Create system login (if this role needs one)
            </Checkbox>
          </Space>
        )}
      </Modal>

      {/* ── One-time password modal ───────────────────────────────────── */}
      <Modal
        title="Login created"
        open={passwordResult !== null}
        onCancel={() => setPasswordResult(null)}
        footer={[
          <Button key="done" type="primary" onClick={() => setPasswordResult(null)}>
            Done
          </Button>,
        ]}
      >
        {passwordResult?.login && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Text>
              Login created for{' '}
              <Text strong>{passwordResult.login.username}</Text> (
              {passwordResult.designation_name}).
            </Text>
            <Card size="small">
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text type="secondary">Temporary password</Text>
                <Text strong copyable code style={{ fontSize: 16 }}>
                  {passwordResult.login.temp_password}
                </Text>
              </Space>
            </Card>
            <Text type="warning">
              Give this to the worker — they must change it on first login.
              This password will not be shown again.
            </Text>
          </Space>
        )}
      </Modal>
    </>
  )
}

// ── Drawer helper component ─────────────────────────────────────────

function OrphanSection({
  title, entries,
}: {
  title: string
  entries: { designation_code: string; designation_name: string; suggested_action: string }[]
}) {
  return (
    <Card size="small" title={title}>
      {entries.map((e) => (
        <div key={e.designation_code} style={{ marginBottom: 8 }}>
          <Text strong>{e.designation_name}</Text>{' '}
          <Text code>{e.designation_code}</Text>
          <div>
            <Text type="secondary">{e.suggested_action}</Text>
          </div>
        </div>
      ))}
    </Card>
  )
}

// ── Helpers ─────────────────────────────────────────────────────────

/** Collect node IDs (keys) for every ancestor up to `maxLevel`, so the
 * AntD Tree defaults to expanded down to that depth. Used to show the
 * Chairman → MD → CEO → CFO/CHRO/COO/CTO band without the operator
 * leaves spilling onto the page. */
function collectKeysToLevel(roots: DesignationNode[], maxLevel: number): React.Key[] {
  const keys: React.Key[] = []
  const walk = (n: DesignationNode, depth: number) => {
    if (depth < maxLevel) {
      keys.push(n.id)
      n.children.forEach((c) => walk(c, depth + 1))
    }
  }
  roots.forEach((r) => walk(r, 1))
  return keys
}
